import { useRouter } from 'next/router';
import { UIEdit, UIMenuSection, UISandboxMenuSection } from '@creoate/virgo-content-api';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { SxProps, Theme } from '@mui/system';
import ListItem from '@mui/material/ListItem';

// constants
import ROUTES from 'constants/routes';
import WP_ROUTES from 'constants/wordpress-routes';

// utils/hooks
import useAuth from 'hooks/useAuth';
import useReferrals from 'hooks/useReferrals';
import useYieldToNextPaint from 'hooks/useYieldToNextPaint';

// components
import Logo from 'components/Logo';
import KycButton from 'components/Header/KycButton';
import LanguagePicker from 'components/LanguagePicker';
import SearchBar from 'components/Header/SearchBar/SearchBar';
import { renderKycInfo } from 'utils/functions';
import TopMenuMobileLink from './TopMenuMobileLink';
import { TopMenuHomeTypes } from '../utils/types';

const styles: { [key: string]: SxProps<Theme> } = {
  headerRoot: {
    height: '100%',
    overflow: 'hidden',
  },
  searchBar: {
    height: '40px',
    width: '100%',
  },
  creditLabel: {
    flexGrow: 1,
    textTransform: 'uppercase',
    boxShadow: 4,
    height: 40,
    width: '100%',
    borderRadius: 1,
  },
  headerAuthenticatedArea: {
    height: '60px !important',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 26px',
  },
};

const TopMenuHome = ({ topMenu: topMenuResponse = [], handleTopLevelClick }: TopMenuHomeTypes) => {
  const router = useRouter();
  const { user, isAuthenticated, userCredit, logout } = useAuth();

  // Use the custom hook for referrals
  const { queryParamsString } = useReferrals();
  const yieldToNextPaint = useYieldToNextPaint();

  const handleLogoutClick = async () => {
    await yieldToNextPaint();
    logout(true, WP_ROUTES.logout);
  };

  return (
    <Stack sx={styles.headerRoot}>
      {/* Header */}
      <Stack sx={styles.headerAuthenticatedArea}>
        <Logo height={60} width={198} sx={{ objectFit: 'cover' }} />

        <LanguagePicker />
      </Stack>

      {isAuthenticated && renderKycInfo(user, userCredit) && (
        <ListItem
          sx={{
            padding: '4px 16px',
          }}
        >
          <KycButton data-testid="mobile-top-menu-kyc-button" sx={styles.creditLabel} />
        </ListItem>
      )}
      <ListItem
        sx={{
          padding: '4px 16px',
        }}
      >
        <SearchBar sx={styles.searchBar} />
      </ListItem>

      <Box sx={{ height: '100%', overflowY: 'auto' }}>
        <Stack sx={{ padding: '0 21px' }}>
          {topMenuResponse?.map((topMenu, index) => {
            if (!isAuthenticated && !topMenu.availableForAnonymousUser) return null;

            if (topMenu.component === 'ui.edit') {
              const { link, isHighlighted } = topMenu as UIEdit;
              return (
                <TopMenuMobileLink
                  isHighlighted={isHighlighted}
                  key={`nav-item-${index + 1}-${topMenu.component}`}
                  label={link?.label}
                  href={link?.url || '#'}
                />
              );
            }

            const { label, component, isHighlighted } = topMenu as UIMenuSection | UISandboxMenuSection;
            return (
              <TopMenuMobileLink
                isHighlighted={isHighlighted}
                key={`nav-item-${index + 1}-${topMenu.component}`}
                label={label}
                handleOnClick={() => {
                  handleTopLevelClick({ index, component, label });
                }}
                showRightIcon
              />
            );
          })}
        </Stack>

        <Divider />

        <Stack sx={{ padding: '0 21px' }}>
          {isAuthenticated ? (
            <>
              <TopMenuMobileLink href="/for-you/brands-you-follow?tab=brands&reference=follow" label="Brand I Follow" />
              <TopMenuMobileLink href="/for-you/wishlist?tab=products&reference=wishlist" label="My Wishlist" />
              <TopMenuMobileLink href="https://helpcenter.creoate.com/im-a-buyer" label="Help Centre" />
              <TopMenuMobileLink handleOnClick={handleLogoutClick} label="Logout" />
            </>
          ) : (
            <>
              <TopMenuMobileLink href={`${ROUTES.auth.login}?redirectTo=${router.asPath}`} label="Login" />
              <TopMenuMobileLink href={`${WP_ROUTES.register}?${queryParamsString}`} label="Sign Up" />
              <TopMenuMobileLink href="/signup/wholesaler/" label="Sell on CREOATE" />
            </>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default TopMenuHome;
